import { useMemo } from "react";
import { formatTimeAgo } from "../../../utils/time";
import { pickAvatarColor } from "../../../utils/avatar";
import type { AgentChat, AgentModuleData, EpisodeListItem } from "../types";
import { useEpisodesListQuery } from "../queries";
import { isNeedsReplyStatus } from "../helpers";

function mapEpisodeToAgentChat(ep: EpisodeListItem): AgentChat {
  // Plan item #8: no context_key — use a generic icon. Icons derived from the
  // episode's started_with target schema_id live on EpisodeDetail surfaces.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const activityAt = ep.date ?? ep.last_message_at ?? ep.updated_at ?? ep.created_at;
  return {
    id: ep.id,
    title: ep.title,
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    preview: `${ep.message_count} messages`,
    time: formatTimeAgo(activityAt),
    activityAt,
    color: pickAvatarColor(ep.title),
    icon: "message-circle",
    status: ep.status,
  };
}

export function useAgentData(): AgentModuleData {
  const { data: episodes } = useEpisodesListQuery();

  const allEpisodes = useMemo(
    () => (episodes ?? []).map(mapEpisodeToAgentChat),
    [episodes],
  );

  const needsReplyEpisodes = useMemo(
    () => allEpisodes.filter((ep) => isNeedsReplyStatus(ep.status)),
    [allEpisodes],
  );

  const canonicalEpisodes = useMemo(
    () => allEpisodes.filter((ep) => ep.status !== "completed"),
    [allEpisodes],
  );

  return {
    listTitle: "Chats",
    searchPlaceholder: "Search conversations...",
    detailSubtitle: "Needs reply",
    roleLabels: { user: "You", assistant: "Agent" },
    composerPlaceholder: "Message agent...",
    chats: needsReplyEpisodes,
    episodes: canonicalEpisodes,
    messagesByChat: {},
  };
}
